import { useEffect, useMemo, useState } from 'react';

import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { PmsType, type User } from '@/@types/global';
import type { Patient } from '@/modules/annotator/types';
import { VisitSidebar } from '@/modules/annotator/visitSidebar/VisitSidebar';
import { endpoints } from '@/react-query/endpoints';

import { mixpanelEvents, useTrackEvent } from '../../../utils/mixpanel';
import { ExternalPatientContext, ONBOARDING_VIDEO_KEY } from '../constants';
import { ExternalError } from '../ExternalError';
import { FreeTrialBanner } from '../FreeTrialBanner';
import {
  useGetPracticeAIImpact,
  useGetTrialStatus,
  useIsAfterAIImpactDataCollectionPeriod,
  useIsWithinThreeBusinessDaysOfStart,
  usePracticeAIImpactSummary,
  useUpdateTrialStatus,
} from '../hooks';
import type { ImageSelectedMessage } from '../types';
import { IframeMessageType, ModalType, TrialStatus } from '../types';
import { useExternalPatientData } from '../useExternalPatientData';

import { AIImpactButton } from './AIImpactButton';
import { ManageSubscription } from './ManageSubscription';
import { OnboardingBanner } from './OnboardingBanner';
import { TrialExpiredCollapsible } from './TrialExpiredCollapsible';
import { TrialOfferCollapsible } from './TrialOfferCollapsible';
import { TrialOfferDetails } from './TrialOfferDetails';
import { VisitSidebarMenu } from './VisitSidebarMenu';

const SidePanelLayout = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={classNames(
      'vai-w-full vai-h-full vai-bg-sidebar-background vai-flex vai-flex-col',
      className,
    )}
  >
    {children}
  </div>
);

export const ExternalVisitSidebarHandler = () => {
  const location = useLocation();
  const { trackEvent } = useTrackEvent();
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const reduxPatients = useSelector(
    (state: { annotator?: { patients?: Record<string, Patient> } }) =>
      state.annotator?.patients,
  );

  const user = useSelector(
    (state: { login?: { user?: User } }) => state.login?.user,
  );
  const pmsType = user?.practice?.pmsType;
  const primaryPracticeId = user?.primaryPracticeId;
  const primaryOrganizationId = user?.primaryOrganizationId;

  const urlParams = new URLSearchParams(location.search);
  const examId = urlParams.get('examId');
  const externalPatientId = urlParams.get('externalPatientId');

  const [isOpenOnboardingBanner, setIsOpenOnboardingBanner] = useState(true);
  const [isManageSubscription, setIsManageSubscription] = useState(false);
  const [externalImageId, setExternalImageId] = useState<string | null>(
    urlParams.get('imageId'),
  );
  const [showTrialDetails, setShowTrialDetails] = useState(false);

  const {
    mutateAsync: updateTrialStatus,
    isLoading: isTrialMutationLoading,
    isError: isTrialMutationError,
    isSuccess: isTrialMutationSuccess,
  } = useUpdateTrialStatus();

  const {
    data: trialStatusData,
    isLoading: isLoadingTrialStatus,
    isError: isErrorTrialStatus,
  } = useGetTrialStatus();

  const trialStatus = trialStatusData?.status;
  const isTrialOffered = trialStatus === TrialStatus.TRIAL_ELIGIBLE;
  const isTrialExpired =
    trialStatus === TrialStatus.TRIAL_EXPIRED ||
    trialStatus === TrialStatus.CANCEL_REQUEST_PAID;
  const isTrialOfferedOrExpired = isTrialOffered || isTrialExpired;

  const {
    imageIdData,
    patientIdData,
    isLoadingPatientData,
    isErrorPatientData,
    visitSummaries,
    latestImages,
    isLoadingVisitAndImageData,
    isErrorVisitAndImageData,
    isSuccess,
  } = useExternalPatientData({
    externalPatientId,
    externalImageId,
    enabled: !isLoadingTrialStatus && !isTrialOfferedOrExpired,
  });

  const {
    data: practiceAIImpactData,
    isLoading: isLoadingPracticeAIImpact,
    isError: isErrorPracticeAIImpact,
  } = useGetPracticeAIImpact();

  const isAfterAIImpactDataCollectionPeriod =
    useIsAfterAIImpactDataCollectionPeriod(trialStatusData);

  const { isPracticeAIImpactAvailable } =
    usePracticeAIImpactSummary(practiceAIImpactData);

  const isDataLoaded = isSuccess && !!trialStatusData;

  // When trial offered/expired, patient data queries are disabled intentionally.
  // We still need to notify the parent frame that content is loaded.
  const isTrialOfferedOrExpiredLoaded =
    !isLoadingTrialStatus && !isErrorTrialStatus && isTrialOfferedOrExpired;

  const showPracticeAIImpact =
    isAfterAIImpactDataCollectionPeriod &&
    isPracticeAIImpactAvailable &&
    !isLoadingPracticeAIImpact &&
    !isErrorPracticeAIImpact;
  const activeImage = useMemo(() => {
    const images = Object.values(
      reduxPatients?.[patientIdData?.patientId ?? '']?.images ?? {},
    );
    return images?.find((image) => image.imageId === imageIdData?.videaImageId);
  }, [imageIdData?.videaImageId, patientIdData?.patientId, reduxPatients]);

  useEffect(() => {
    if (
      user?.practice && // we want the PMS type to be present, so have to make sure that practice is present
      (!externalPatientId ||
        !externalImageId ||
        isDataLoaded ||
        isTrialOfferedOrExpiredLoaded ||
        isErrorPatientData ||
        isErrorVisitAndImageData ||
        isErrorTrialStatus)
    ) {
      trackEvent(mixpanelEvents.externalPmsEntry, {
        source: 'visit-sidebar',
        externalPatientId,
        externalImageId,
        examId,
        patientId: patientIdData?.patientId ?? null,
        imageId: imageIdData?.videaImageId ?? null,
        trialStatus: trialStatusData?.status ?? null,
        centralizedBilling: trialStatusData?.centralizedBilling ?? null,
        isIframe: true,
      });
    }
    // Disabling exhaustive deps so that event is only tracked once after internal patient and image data is loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDataLoaded,
    isTrialOfferedOrExpiredLoaded,
    isErrorPatientData,
    isErrorVisitAndImageData,
    isErrorTrialStatus,
    user?.practice,
  ]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent<ImageSelectedMessage>) => {
      const eventType = event.data?.type;
      if (eventType !== IframeMessageType.IMAGE_SELECTED) return;

      const payload = event.data?.payload;
      const newExternalImageId = payload?.imageId;
      if (newExternalImageId) {
        trackEvent(mixpanelEvents.externalImageSelected, {
          externalPatientId,
          externalImageId: newExternalImageId,
          patientId: patientIdData?.patientId ?? null,
          trialStatus: trialStatusData?.status ?? null,
          centralizedBilling: trialStatusData?.centralizedBilling ?? null,
          isIframe: true,
        });
        setExternalImageId(newExternalImageId);
      } else {
        console.warn('No image ID received');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [
    externalPatientId,
    patientIdData?.patientId,
    trackEvent,
    trialStatusData?.status,
    trialStatusData?.centralizedBilling,
  ]);

  useEffect(() => {
    // Once all data is loaded, send message to notify RCI that images can now be selected
    // isTrialOfferedOrExpiredLoaded handles the case when patient data queries are intentionally disabled
    if (
      isDataLoaded ||
      isTrialOfferedOrExpiredLoaded ||
      isErrorPatientData ||
      isErrorVisitAndImageData ||
      isErrorTrialStatus
    ) {
      trackEvent(mixpanelEvents.externalContentLoaded, {
        externalPatientId,
        externalImageId,
        patientId: patientIdData?.patientId ?? null,
        imageId: imageIdData?.videaImageId ?? null,
        trialStatus: trialStatusData?.status ?? null,
        centralizedBilling: trialStatusData?.centralizedBilling ?? null,
        isIframe: true,
      });
      window.parent.postMessage(
        {
          type: IframeMessageType.CONTENT_LOADED,
          payload: {},
        },
        '*',
      );
    }
    // Disabling exhaustive deps so that message is only sent when the content first loads
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDataLoaded,
    isTrialOfferedOrExpiredLoaded,
    isErrorPatientData,
    isErrorVisitAndImageData,
    isErrorTrialStatus,
  ]);

  const isFreeTrial = trialStatus === TrialStatus.TRIAL;
  const showBanner =
    isFreeTrial ||
    (!!trialStatusData?.centralizedBilling &&
      trialStatus === TrialStatus.PAID_BUT_PROVISIONAL);
  const isWithinThreeBusinessDaysOfStart =
    useIsWithinThreeBusinessDaysOfStart(trialStatusData);
  const isOnboardingVideoWatched = !!localStorage.getItem(ONBOARDING_VIDEO_KEY);
  const showOnboardingBanner =
    isWithinThreeBusinessDaysOfStart &&
    isOpenOnboardingBanner &&
    !isOnboardingVideoWatched;

  const handleTrialBack = () => {
    trackEvent(mixpanelEvents.externalCollapsibleToggle, {
      externalPatientId,
      externalImageId,
      patientId: patientIdData?.patientId ?? null,
      imageId: imageIdData?.videaImageId ?? null,
      isOpen: false,
      source: 'TRIAL_OFFER',
      action: 'BACK',
      trialStatus: trialStatusData?.status ?? null,
      centralizedBilling: trialStatusData?.centralizedBilling ?? null,
      isIframe: true,
    });
    setShowTrialDetails(false);
  };

  const handleTrialLearnMore = () => {
    trackEvent(mixpanelEvents.externalTrialOfferLearnMore, {
      externalPatientId,
      externalImageId,
      patientId: patientIdData?.patientId ?? null,
      imageId: imageIdData?.videaImageId ?? null,
      trialStatus: trialStatusData?.status ?? null,
      centralizedBilling: trialStatusData?.centralizedBilling ?? null,
      isIframe: true,
    });
    setShowTrialDetails(true);
  };

  const handleOpenOnboarding = (source: string) => {
    trackEvent(mixpanelEvents.externalOnboardingModalOpen, {
      externalPatientId,
      externalImageId,
      patientId: patientIdData?.patientId ?? null,
      imageId: imageIdData?.videaImageId ?? null,
      trialStatus: trialStatusData?.status ?? null,
      centralizedBilling: trialStatusData?.centralizedBilling ?? null,
      isIframe: true,
      source,
    });
    window.parent.postMessage(
      {
        type: IframeMessageType.MODAL_OPEN,
        payload: {
          type: ModalType.ONBOARDING,
          patientId: patientIdData?.patientId ?? null,
          imageId: imageIdData?.videaImageId ?? null,
          externalPatientId,
          externalImageId,
          source,
        },
      },
      '*',
    );
  };

  const handleStartTrial = async () => {
    try {
      await updateTrialStatus({ status: TrialStatus.TRIAL });

      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Open onboarding modal automatically after trial start
      handleOpenOnboarding('AUTOMATIC_OPEN_AFTER_TRIAL_START');
    } catch (error) {
      // Error is handled by React Query and displayed in the UI via isTrialError state
      console.error('Failed to start trial:', error);
    }
  };

  const getLogo = () => {
    return pmsType === PmsType.DENTRIX_ASCEND ? (
      <img
        alt="Dentrix Ascend VideaAI Logo"
        className="vai-h-5 [@media(max-height:570px)]:vai-h-4 vai-self-end"
        src="/assets/images/ddai/2025-dentrix-ascend-videaai.svg"
      />
    ) : (
      <img
        alt="Dentrix VideaAI Logo"
        className="vai-h-5 [@media(max-height:570px)]:vai-h-4 vai-self-end"
        src="/assets/images/ddai/2025-dentrix-videaai.svg"
      />
    );
  };

  const visitSidebarView = () => {
    if (isLoadingTrialStatus) {
      return (
        <SidePanelLayout className="vai-p-4 vai-flex vai-items-center vai-justify-center">
          <div className="vai-animate-spin vai-rounded-full vai-h-8 vai-w-8 vai-border-[3px] vai-border-t-transparent vai-border-sapphire-600" />
        </SidePanelLayout>
      );
    }

    if (isErrorTrialStatus) {
      return (
        <SidePanelLayout className="vai-p-4 vai-flex vai-items-center vai-justify-center">
          <ExternalError
            message={t`Please refresh the panel. If the problem continues, contact support for help.`}
            source="VISIT_SIDEBAR"
            title={t`Trouble loading content.`}
          />
        </SidePanelLayout>
      );
    }

    // Show trial offer/expired UI even if patient/image data failed to load
    if (isTrialOfferedOrExpired) {
      return (
        <SidePanelLayout className="vai-p-4">
          <div className="vai-flex vai-items-center vai-gap-3 vai-justify-between">
            <div className="vai-flex vai-items-center vai-gap-1">
              {showTrialDetails && (
                <button
                  className="vai-flex vai-items-center vai-gap-1 vai-text-white hover:vai-text-slate-200"
                  onClick={handleTrialBack}
                  type="button"
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                  <span className="vai-font-sans vai-text-sm">
                    <Trans>Back</Trans>
                  </span>
                </button>
              )}
            </div>
            {getLogo()}
          </div>
          <div className="vai-flex-1" />
          {isTrialOffered && (
            <div className="vai-relative vai-overflow-hidden">
              <div
                className={classNames(
                  'vai-transition-all vai-duration-300 vai-ease-out',
                  showTrialDetails
                    ? 'vai-opacity-0 vai-pointer-events-none vai-absolute vai-inset-0 -vai-translate-x-full'
                    : 'vai-opacity-100 vai-translate-x-0',
                )}
              >
                <TrialOfferCollapsible
                  isCentralizedBilling={trialStatusData?.centralizedBilling}
                  trialStatus={trialStatusData?.status}
                  onMoreInfo={handleTrialLearnMore}
                  trialLengthInDays={trialStatusData.trialLengthInDays}
                  onStartTrial={handleStartTrial}
                  isTrialLoading={isTrialMutationLoading}
                  isTrialError={isTrialMutationError}
                  isTrialSuccess={isTrialMutationSuccess}
                  onOpenOnboarding={() =>
                    handleOpenOnboarding('BUTTON_CLICK_AFTER_TRIAL_START')
                  }
                />
              </div>
              <div
                className={classNames(
                  'vai-transition-all vai-duration-300 vai-ease-out',
                  showTrialDetails
                    ? 'vai-opacity-100 vai-translate-x-0'
                    : 'vai-opacity-0 vai-translate-x-full vai-pointer-events-none vai-absolute vai-inset-0',
                )}
              >
                <TrialOfferDetails
                  isCentralizedBilling={trialStatusData?.centralizedBilling}
                  trialStatus={trialStatusData?.status}
                  trialLengthInDays={trialStatusData.trialLengthInDays}
                  onStartTrial={handleStartTrial}
                  isTrialLoading={isTrialMutationLoading}
                  isTrialError={isTrialMutationError}
                  isTrialSuccess={isTrialMutationSuccess}
                  onOpenOnboarding={() =>
                    handleOpenOnboarding('BUTTON_CLICK_AFTER_TRIAL_START')
                  }
                />
              </div>
            </div>
          )}
          {isTrialExpired && (
            <TrialExpiredCollapsible
              isCentralizedBilling={trialStatusData?.centralizedBilling}
              showPracticeAIImpact={showPracticeAIImpact}
              trialStatus={trialStatusData?.status}
            />
          )}
        </SidePanelLayout>
      );
    }

    // For non-trial offered/expired states, we need patient/image data
    if (!externalPatientId || !externalImageId) {
      return (
        <SidePanelLayout className="vai-p-4 vai-flex vai-items-center vai-justify-center">
          <ExternalError
            message={t`We didn't receive enough information about the patient or image.`}
            source="VISIT_SIDEBAR"
            title={t`Not enough information.`}
          />
        </SidePanelLayout>
      );
    }

    if (isErrorPatientData) {
      return (
        <SidePanelLayout className="vai-p-4 vai-flex vai-items-center vai-justify-center">
          <ExternalError
            message={t`The patient information we received didn't match a patient in Detect AI.`}
            source="VISIT_SIDEBAR"
            title={t`We can't find this patient.`}
          />
        </SidePanelLayout>
      );
    }

    if (isErrorVisitAndImageData) {
      return (
        <SidePanelLayout className="vai-p-4 vai-flex vai-items-center vai-justify-center">
          <ExternalError
            message={t`Please refresh the panel. If the problem continues, contact support for help.`}
            source="VISIT_SIDEBAR"
            title={t`Trouble loading content.`}
          />
        </SidePanelLayout>
      );
    }

    if (isManageSubscription) {
      return (
        <SidePanelLayout>
          <ManageSubscription
            isLoading={isLoadingTrialStatus}
            onClose={() => {
              trackEvent(mixpanelEvents.externalManageSubscriptionClose, {
                externalPatientId,
                externalImageId,
                patientId: patientIdData?.patientId ?? null,
                imageId: imageIdData?.videaImageId ?? null,
                trialStatus: trialStatusData?.status ?? null,
                centralizedBilling: trialStatusData?.centralizedBilling ?? null,
                isIframe: true,
              });
              setIsManageSubscription(false);
            }}
            trialStatusData={trialStatusData}
          />
        </SidePanelLayout>
      );
    }

    return (
      <SidePanelLayout>
        {showBanner && <FreeTrialBanner trialStatusData={trialStatusData} />}

        <div className="vai-relative vai-flex-1 vai-pt-4 [@media(max-height:570px)]:vai-pt-2 vai-flex vai-flex-col vai-justify-between vai-overflow-auto">
          <div className="vai-px-2 vai-flex vai-items-center vai-justify-between">
            {getLogo()}
            <div className="vai-flex vai-items-center vai-gap-2">
              {isFreeTrial && showPracticeAIImpact && <AIImpactButton />}
              <VisitSidebarMenu
                showPracticeAIImpact={showPracticeAIImpact}
                trialStatus={trialStatus}
                onManageSubscription={() => {
                  trackEvent(mixpanelEvents.externalManageSubscriptionOpen, {
                    externalPatientId,
                    externalImageId,
                    patientId: patientIdData?.patientId ?? null,
                    imageId: imageIdData?.videaImageId ?? null,
                    trialStatus: trialStatusData?.status ?? null,
                    centralizedBilling:
                      trialStatusData?.centralizedBilling ?? null,
                    isIframe: true,
                  });
                  setIsManageSubscription(true);
                }}
              />
            </div>
          </div>

          <VisitSidebar
            activeImage={activeImage}
            dob={latestImages?.dob}
            isExpandedSidebar
            isLoading={isLoadingPatientData || isLoadingVisitAndImageData}
            visitSummary={visitSummaries?.[0]}
          />

          {showOnboardingBanner && (
            <OnboardingBanner
              trialStatusData={trialStatusData}
              onClose={() => {
                setIsOpenOnboardingBanner(false);
              }}
            />
          )}
        </div>
      </SidePanelLayout>
    );
  };

  return (
    <ExternalPatientContext.Provider
      value={{
        isIframe: true,
        externalPatientId,
        externalImageId,
        patientId: patientIdData?.patientId ?? null,
        imageId: imageIdData?.videaImageId ?? null,
        trialStatus: trialStatusData?.status ?? null,
        centralizedBilling: trialStatusData?.centralizedBilling ?? null,
      }}
    >
      {visitSidebarView()}
    </ExternalPatientContext.Provider>
  );
};
